import { isRef } from 'vue'
import type { ThemeInstance } from 'vuetify'

/**
 * Typed accessors for app-level state owned by framework singletons rather
 * than by any one component: the Vuetify theme and the public runtime config.
 * Centralizing the theme reads/writes here is what makes the Vuetify 2 → 3
 * theme-API change (`this.$vuetify.theme.dark` →
 * `useTheme().global.current.value.dark`) a one-file repoint instead of ~158
 * scattered call-site edits.
 *
 * WHY A CAPTURED SINGLETON, NOT `useTheme()` DIRECTLY: `useTheme()` is a
 * composable — it `inject()`s the theme, so it may only be called from a
 * component `setup()`. But `isDarkTheme()` is called from ~50 computed getters
 * and from non-setup code (mixins, the SiloOverview factory), where
 * `useTheme()` would throw. So plugins/03.theme.client.ts captures the one
 * app-wide `ThemeInstance` at boot (via the vuetify-nuxt-module `vuetify:ready`
 * hook) and hands it here through `setThemeInstance()`. The app is SPA-only
 * (`ssr: false`) → exactly one theme instance for the life of the browser
 * session, so a module singleton is safe (same rationale as the store/router
 * singletons in cynqueModules/nuxtRuntime.ts).
 *
 * REACTIVITY: `theme.global.current` is a Vue computed ref over the active
 * theme definition (or a reactive value, if a reactive-unwrapped view was
 * captured), so reading its `.dark` from inside a Vue computed still registers
 * the reactive dependency — callers re-evaluate when dark mode toggles. For
 * that reason these MUST remain plain live reads on every call: never cache or
 * memoize the value, and never read it once at module load.
 */

let _theme: ThemeInstance | undefined

/**
 * Register the app-wide Vuetify theme instance. Called once from
 * plugins/03.theme.client.ts after Vuetify is created. Until it runs (very
 * early boot, or a unit test with no Vuetify), the accessors below report the
 * app as light — matching the configured default theme.
 */
export function setThemeInstance(theme: ThemeInstance): void {
  _theme = theme
}

/**
 * Whether the app is in dark mode. A live, reactive read of the active Vuetify
 * theme, safe from component computeds AND non-component code — see the header.
 *
 * `global.current` is a computed Ref on the raw `useTheme()` instance, but a
 * plain reactive value if a reactive-unwrapped view was captured; we read
 * whichever we got (both stay reactive). See plugins/03.theme.client.ts.
 */
export function isDarkTheme(): boolean {
  const current = _theme?.global.current
  if (!current) {
    return false
  }
  const themeDefinition = isRef(current) ? current.value : current
  return !!themeDefinition.dark
}

/**
 * Switch the app between the named 'light' / 'dark' themes (defined in
 * nuxt.config.ts `vuetify.theme.themes`). Used by the two layout store-sync
 * writes (`layouts/default.vue`, `layouts/login.vue`) and the dark-mode
 * switch's model setter.
 *
 * Uses the ThemeInstance `change()` method rather than assigning
 * `global.name.value`: `change()` works whether the captured instance exposes
 * `global.name` as a Ref or as a reactive-unwrapped string. No-op until the
 * theme instance is registered.
 */
export function setDarkTheme(value: boolean): void {
  _theme?.change(value ? 'dark' : 'light')
}

/**
 * The app's public runtime config (runtimeConfig.public — version, mockApi,
 * log-suppression flags, ...). Nuxt 4's `useRuntimeConfig()` works in component
 * AND non-component code on the client (reads a global payload, no active-Nuxt
 * -instance requirement), so it is safe here. Static config, not reactive.
 */
export function getPublicRuntimeConfig() {
  return useRuntimeConfig().public
}
